import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  createContext,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useState,
  useRef,
  type RefObject,
} from "react";
import Draggable from "react-draggable";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { Button } from "../Button";
import CloseIcon from "../icons/AikaIcons/CloseIcon";
import React from "react";

interface IModalProps {
  title?: ReactNode;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  small?: boolean;
  /**
   * If set, user cannot dismiss modal by clicking outside content,
   * hitting the Escape button, and the close button is hidden.
   */
  dialog?: boolean;
  onErrorReset?: () => void;
}

export const ModalRoot = (props: {
  children: any;
  controller?: {
    open: boolean;
    setOpen: (open: boolean) => void;
    onOpenChange?: (open: boolean) => void;
  };
}) => {
  const { children, controller } = props;
  if (controller)
    return (
      <Dialog.Root
        open={controller.open}
        onOpenChange={
          controller.onOpenChange
            ? controller.onOpenChange
            : (open) => controller.setOpen(open)
        }
      >
        {children}
      </Dialog.Root>
    );
  return <Dialog.Root>{children}</Dialog.Root>;
};

interface IModalTriggerProps {
  disabled?: boolean;
  children?: ReactNode;
  asChild?: boolean;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}
export const ModalTrigger = React.forwardRef<
  HTMLButtonElement,
  IModalTriggerProps
>((props: IModalTriggerProps, ref) => {
  return (
    <Dialog.Trigger
      disabled={props.disabled}
      ref={ref}
      className={props.className}
      asChild={props.asChild}
      onClick={props.onClick}
    >
      {props.children}
    </Dialog.Trigger>
  );
});

export const Modal = (props: IModalProps) => {
  const {
    title,
    description,
    children,
    footer,
    dialog,
    className,
    small,
    onErrorReset,
  } = props;
  const dragRef = useRef<HTMLDivElement>(null);
  return (
    <Dialog.Portal>
      <Dialog.Overlay
        className={`c-modal-overlay${small ? " c-modal-overlay--sm" : ""}`}
      >
        <Draggable
          nodeRef={dragRef as RefObject<HTMLElement>}
          handle=".draggable-handle"
          bounds="body"
          defaultPosition={{ x: 0, y: small ? 0 : 84 }}
        >
          <Dialog.Content
            ref={dragRef}
            className={`c-modal ${className ?? ""}`}
            onInteractOutside={(e) => {
              if (dialog) e.preventDefault();
            }}
            onEscapeKeyDown={(e: KeyboardEvent) => {
              if (dialog) e.preventDefault();
            }}
            onClick={(e: ReactMouseEvent<HTMLDivElement, MouseEvent>) =>
              e.stopPropagation()
            }
          >
            <ErrorBoundary
              FallbackComponent={ModalErrorFallback}
              onError={onErrorReset}
            >
              {title || !dialog ? (
                <header className="c-modal-header draggable-handle">
                  <Dialog.Title className="c-modal-title display-4 fw-semibold text-muted">
                    {title}
                  </Dialog.Title>
                  {!dialog && (
                    <Dialog.Close className="btn btn-circle">
                      <CloseIcon />
                    </Dialog.Close>
                  )}
                  {description && (
                    <VisuallyHidden asChild>
                      <Dialog.Description>{description}</Dialog.Description>
                    </VisuallyHidden>
                  )}
                </header>
              ) : null}
              <div className="c-modal-content">
                <div
                  className={`c-modal-container ${
                    small ? "c-modal-container--sm" : ""
                  }`}
                >
                  {children}
                </div>
              </div>
              {footer && <div className="c-modal-footer">{footer}</div>}
            </ErrorBoundary>
          </Dialog.Content>
        </Draggable>
      </Dialog.Overlay>
    </Dialog.Portal>
  );
};

const ModalErrorFallback = (props: FallbackProps) => {
  return (
    <>
      <header className="c-modal-header draggable-handle">
        <Dialog.Title className="c-modal-title">Runtime error</Dialog.Title>
      </header>
      <div className="c-modal-content">
        <div className="c-modal-container">
          {" "}
          <div className="c-modal-container-inner">
            <p>Noget gik galt i modalen</p>
            {props.error instanceof Error && props.error.message && (
              <p>{props.error.message}</p>
            )}
          </div>
          <div className="c-modal-footer">
            {props.resetErrorBoundary && (
              <Button color="primary" onClick={props.resetErrorBoundary}>
                Tilbage
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export const ModalContext = createContext<{
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
}>({
  modalOpen: false,
  setModalOpen: (_open) => {},
});

export const ModalProvider = (props: { children: ReactNode }) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  return (
    <ModalContext.Provider value={{ modalOpen, setModalOpen }}>
      <ModalRoot controller={{ open: modalOpen, setOpen: setModalOpen }}>
        {props.children}
      </ModalRoot>
    </ModalContext.Provider>
  );
};
export default Modal;
